<template>
  <div v-if="returnCase" class="return-case-cnt">
    <div class="details-container">
      <CaseHeader :case-order="returnCase" :case-not-submitted="isEditMode" />
      <component
        :is="activeFormComponent"
        :return-case="returnCase"
        :defect-reason-options="defectReasonOptions"
        :is-editable="props.isEditMode"
        @form-is-valid="emit('form-is-valid', $event)"
        @update-case="onUpdateCase" />
    </div>

    <OneTimeAddress
      :is-enabled="isEditMode"
      :account-address="returnCase.address"
      :title="addressModalLocalisation.title"
      class="address-container"
      :button-label="addressModalLocalisation.buttonLabel"
      @delivery-address-updated="(val) => updateCaseField('address', val as AccountAddress)">
    </OneTimeAddress>
  </div>

  <div class="return-items">
    <AgGridVue
      class="ag-theme-balham"
      data-cy="cases-table"
      :grid-options="gridOptions"
      :column-defs="columnDefinitions"
      :default-col-def="defaultColDef"
      :row-data="returnCase.lines"
      :modules="[ClientSideRowModelModule]">
    </AgGridVue>
  </div>
</template>

<script lang="ts" setup>
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import type { ColDef } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, toRef } from 'vue';
import { useI18n } from 'vue-i18n';

import CaseHeader from '@/components/cases/CaseHeader.vue';
import PickupReturnCaseForm from '@/components/cases/PickupReturnCaseForm.vue';
import ReturnCaseForm from '@/components/cases/ReturnCaseForm.vue';
import OneTimeAddress from '@/components/common/OneTimeAddress.vue';
import { useFormat } from '@/composables/format';
import { useGrid } from '@/composables/grid';
import { useCaseUpdater } from '@/composables/useCaseUpdater';
import type { AccountAddress } from '@/models/account-address.model';
import type { CaseLine } from '@/models/case-line.model';
import type { DefectReason } from '@/models/defect-reason.model';
import { returnRequestTypeByLegalEntity } from '@/models/return-request-type-by-region.model';
import { CasesService } from '@/services/cases.service';
import { useUserStore } from '@/stores/user';
import type { ReturnCase } from '@/types/case.interface';
import { CaseReturnRequestType, CaseType, ProductMainGroups } from '@onrunning/backstage-models';
import type { SelectFieldOption } from '../common/SelectField.vue';

const props = defineProps<{
  isEditMode: boolean;
  returnCase: ReturnCase;
}>();

const emit = defineEmits<{
  'update-case': [returnCase: ReturnCase];
  'form-is-valid': [isValid: boolean];
}>();

// Services
const casesService = new CasesService();

const userStore = useUserStore();
const { selectedAccount: account } = storeToRefs(userStore);

const { t } = useI18n();
const { defaultGridOptions } = useGrid();
const { getTranslationFromTranslateField } = useFormat();
const { updateCaseField } = useCaseUpdater(toRef(props, 'returnCase'), (updatedCase) => onUpdateCase(updatedCase));

const defectReasons = ref<DefectReason[]>([]);
const gridOptions = ref(defaultGridOptions());
const defaultColDef = ref<ColDef>({ resizable: true, sortable: true, minWidth: 80, flex: 1, cellDataType: false });

const activeFormComponent = computed(() => {
  return isPickupRequest.value ? PickupReturnCaseForm : ReturnCaseForm;
});

const isPickupRequest = computed(() => {
  if (!props.returnCase) return false;

  const totalShoeCount = props.returnCase.lines.reduce((total: number, line: CaseLine) => {
    if (line.productMainGroup === ProductMainGroups.shoes) {
      return total + (line.quantity ?? 0);
    }

    return total;
  }, 0);

  const returnRequestType =
    returnRequestTypeByLegalEntity[account.value.legalEntity] ?? returnRequestTypeByLegalEntity.default;

  return (
    props.returnCase.type === CaseType.Return &&
    returnRequestType.requestType === CaseReturnRequestType.Pickup &&
    totalShoeCount > returnRequestType.min
  );
});

const addressModalLocalisation = computed(() => {
  return isPickupRequest.value
    ? {
        title: t('oneTimeAddress.pickup_request.title'),
        buttonLabel: t('oneTimeAddress.pickup_request.btn_label'),
      }
    : {
        title: t('oneTimeAddress.label_request.title'),
        buttonLabel: t('oneTimeAddress.label_request.btn_label'),
      };
});

const columnDefinitions = computed(() => [
  { headerValueGetter: () => t('viewCase.item_number'), field: 'productNumber' },
  { headerValueGetter: () => t('viewCase.item_product_group'), field: 'productGroupName' },
  { headerValueGetter: () => t('viewCase.item_quantity'), field: 'quantity' },
]);

const defectReasonOptions = computed((): SelectFieldOption[] => {
  return defectReasons.value.map(({ type, description }) => ({
    value: type,
    label: getTranslationFromTranslateField(description),
  }));
});

onMounted(() => {
  init();
});

const init = async () => {
  if (props.returnCase) {
    await fetchDefectReasons(props.returnCase.type);
  }
};

const fetchDefectReasons = async (caseType: CaseType) => {
  const result = await casesService.getDefectReasons(caseType, account.value?.billingCountry);

  if (result.model?.length) {
    defectReasons.value = result.model;
  }
};

const onUpdateCase = (updatedCase: ReturnCase) => {
  emit('update-case', updatedCase);
};
</script>
<style scoped lang="scss">
.return-case-cnt {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-start;

  .details-container {
    flex: 2 1 60%;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    background-color: var(--primary-bg-color);
    padding: 1.25rem;
    border-radius: 0.25rem;
  }

  .address-container {
    flex: 1 1 35%;
    background-color: var(--primary-bg-color);
    padding: 1.25rem;
    border-radius: 0.25rem;
  }
}

.return-items {
  @include container-background;
  margin-top: 48px;
}
</style>
